// Whether a conversation's contact name can be trusted.
//
// The contact record is the authority on WHERE a reply goes — Chatwoot addresses
// it to the contact's email — but not on WHO the client is. Threads migrated
// from Intercom resolved the contact's name and address from different actors
// (functions/src/functions/chatwoot/sync/service.ts), so a thread an operator
// had started came over with that operator as the contact name while the address
// stayed the client's. The inbox then listed one of our own people in front of a
// client's address — "Darleen Warda (personal@mandant.de)" — and operators could
// no longer tell who would receive their answer.
//
// Individual messages follow the same rule: the contact name only describes
// the sender when the contact IS that sender.
// Here the giveaway is that the name belongs to one of our agents while the
// address does not, which no real client contact ever looks like. The
// conversation payload carries nothing to recover the client's real name from,
// so the address is shown on its own: less friendly, but honest.

export interface ChatwootAgentIdentity {
  name?: string
  email?: string
}

function normalizeName(value: string | undefined): string | undefined {
  const normalized = value?.trim().replace(/\s+/g, ' ').toLowerCase()
  return normalized || undefined
}

function normalizeEmail(value: string | undefined): string | undefined {
  const normalized = value?.trim().toLowerCase()
  return normalized || undefined
}

// True when `name` is the name of one of our Chatwoot agents but `email` is not
// that agent's address — i.e. the name describes a colleague, not the contact it
// is attached to.
//
// Both fields are required for a verdict: without an address there is nothing to
// contradict the name (a widget contact carries no email at all), and we'd
// rather keep a rare namesake's name than blank out a legitimate contact.
//
// The verdict is taken over the name's matching agents as a whole rather than
// agent by agent, because a single record can't settle it: an agent whose
// directory entry carries no address contradicts nothing, and when two agents
// share a name it takes only one of them to own the contact's address for the
// name to be that contact's own.
export function isForeignAgentName(
  name: string | undefined,
  email: string | undefined,
  agents: ChatwootAgentIdentity[] = []
): boolean {
  const contactName = normalizeName(name)
  const contactEmail = normalizeEmail(email)
  if (!contactName || !contactEmail) {
    return false
  }
  const addressesOfThisName = agents
    .filter((agent) => normalizeName(agent.name) === contactName)
    .map((agent) => normalizeEmail(agent.email))
    .filter((agentEmail): agentEmail is string => !!agentEmail)

  return (
    addressesOfThisName.length > 0 &&
    !addressesOfThisName.includes(contactEmail)
  )
}
